import React, { createContext, useContext, useState, useCallback } from 'react';
import { FilterState } from '../modules/issues/filter/IssueFilters';

interface SearchContextType {
  // Global search state
  globalSearchText: string;
  setGlobalSearchText: (text: string) => void;

  // Filters state (can be updated from both HeaderBar and IssueFilters)
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;

  // Methods to handle search from different components
  handleHeaderSearch: (searchText: string, skipAggregation:boolean) => void;
  handleFilterSearch: (filters: FilterState) => void;

  // Clear all searches
  clearAllSearches: () => void;

  // Active search indicator
  hasActiveSearch: boolean;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

interface SearchProviderProps {
  children: React.ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [filters, setFilters] = useState<FilterState>({
    searchText: '',
    options: [],
    dateRange: null,
    skipAggregation: false,
    hasFilters: false
  });

  // Handle search from HeaderBar
  const handleHeaderSearch = useCallback((searchText: string, skipAggregation:boolean) => {
    setGlobalSearchText(searchText);
    setFilters(prev => ({
      ...prev,
      searchText: searchText,
      skipAggregation: skipAggregation
    }));
  }, []);

  // Handle search from IssueFilters
  const handleFilterSearch = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
    // Update global search if the searchText changed
    if (newFilters.searchText !== filters.searchText) {
      setGlobalSearchText(newFilters.searchText);
    }
  }, [filters.searchText]);

  // Clear all searches
  const clearAllSearches = useCallback(() => {
    setGlobalSearchText('');
    setFilters({
      searchText: '',
      options: [],
      dateRange: null,
      skipAggregation: false,
      hasFilters: true // true when cleared/no filters
    });
  }, []);

  // Check if there's any active search
  const hasActiveSearch = globalSearchText.trim() !== '' ||
                         filters.options.length > 0 ||
                         filters.dateRange !== null;

  const value: SearchContextType = {
    globalSearchText,
    setGlobalSearchText,
    filters,
    setFilters,
    handleHeaderSearch,
    handleFilterSearch,
    clearAllSearches,
    hasActiveSearch
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

// Custom hook to use the search context
export const useSearch = (): SearchContextType => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
